import React, { useMemo, useState, useEffect } from "react";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { Tooltip } from "antd"; // Import Antd Tooltip
import "./Calendar.scss";
import { formatUSD } from "../../utils/helpers/string";

const Calendar = ({ data = {} }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Track screen size for mobile logic
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- 1. Data Processing & Aggregation ---
  const dailyStats = useMemo(() => {
    const stats = {};

    if (!data || typeof data !== "object") return stats;

    Object.keys(data).forEach((dateKey) => {
      const dayData = data[dateKey];
      if (!dayData) return;

      stats[dateKey] = {
        count: dayData.no_of_trades || 0,
        totalProfit: dayData.net_pnl || 0,
        trades: [],
      };
    });

    return stats;
  }, [data]);

  // --- Header Section ---
  const renderHeader = () => {
    const dateFormat = "MMMM yyyy";
    return (
      <div className="header row flex-middle">
        <div className="col col-start">
          <div className="icon" onClick={prevMonth}>
            &#8249;
          </div>
        </div>
        <div className="col col-center">
          <span>{format(currentMonth, dateFormat)}</span>
        </div>
        <div className="col col-end">
          <div className="icon" onClick={nextMonth}>
            &#8250;
          </div>
        </div>
      </div>
    );
  };

  // --- Days Section ---
  const renderDays = () => {
    // Use state variable instead of direct window access
    const dateFormat = isMobile ? "eee" : "eeee"; 
    const days = [];
    let startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="col col-center" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }

    return <div className="days row">{days}</div>;
  };

// --- Cells Section ---
const renderCells = () => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const dateFormat = "d";
  const rows = [];
  let days = [];
  let day = startDate;
  let formattedDate = "";

  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      formattedDate = format(day, dateFormat);
      const dateKey = format(day, "yyyy-MM-dd");
      const cloneDay = day;
      
      const dayStat = dailyStats[dateKey];

      // Determine Status Classes
      const isProfit = dayStat && dayStat.totalProfit >= 0;
      const isLoss = dayStat && dayStat.totalProfit < 0;
      const statusClass = dayStat ? (isProfit ? "has-profit" : "has-loss") : "";

      // 1. Define Stats Content
      const statsContent = dayStat ? (
        <div className="events" style={isMobile ? { marginTop: 0 } : {}}>
           <div className="stat-row">
              <span className="label">Trades:</span>
              <span className="value">{dayStat.count}</span>
           </div>
           <div className={`stat-row pnl ${isProfit ? 'positive' : 'negative'}`}>
              <span className="label">P&L:</span>
              <span className="value">
                  {isProfit ? '+' : ''}{formatUSD(dayStat.totalProfit)}
              </span>
           </div>
        </div>
      ) : null;

      // 2. Define Cell
      const cell = (
        <div
          className={`col cell ${
            !isSameMonth(day, monthStart)
              ? "disabled"
              : isSameDay(day, selectedDate)
              ? "selected"
              : ""
          } ${statusClass}`} // <--- Added statusClass here
          key={day}
          onClick={() => onDateClick(cloneDay)}
        >
          <span className="number">{formattedDate}</span>
          {!isMobile && statsContent}
        </div>
      );

      // 3. Render
      if (isMobile && dayStat) {
          days.push(
              <Tooltip title={statsContent} key={day} placement="top" trigger="hover">
                  {cell}
              </Tooltip>
          );
      } else {
          days.push(cell);
      }

      day = addDays(day, 1);
    }
    rows.push(
      <div className="row" key={day}>
        {days}
      </div>
    );
    days = [];
  }
  return <div className="body">{rows}</div>;
};

  const onDateClick = (day) => {
    setSelectedDate(day);
    const dateKey = format(day, "yyyy-MM-dd");
    console.log("Trades for", dateKey, dailyStats[dateKey]);
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  return (
    <div className="eventCalendar">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
    </div>
  );
};

export default Calendar;